// src/components/cubiepop/CubiePopLanding.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Page = styled.div`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #2a1a40;
    background: linear-gradient(180deg, #F1ECFF 0%, #FFFFFF 100%);
    min-height: 100vh;
`;

const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 4rem 1.5rem;
`;

const Hero = styled.section`
    text-align: center;
    padding: 5rem 1rem 4rem;
`;

const Logo = styled.div`
    display: inline-block;
    background: #7C4DFF;
    color: white;
    font-weight: 900;
    font-size: 1rem;
    letter-spacing: 1px;
    padding: 0.4rem 1rem;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const Title = styled.h1`
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 900;
    margin: 0 0 1rem;
    color: #7C4DFF;
    line-height: 1.1;
`;

const Subtitle = styled.p`
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    color: #6a5a82;
    max-width: 600px;
    margin: 0 auto 2.5rem;
    line-height: 1.6;
`;

const PlayBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: #7C4DFF;
    color: white;
    padding: 0.9rem 2rem;
    border-radius: 999px;
    font-weight: 700;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(124, 77, 255, 0.3);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(124, 77, 255, 0.4);
    }
`;

const Features = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    margin: 4rem 0;
`;

const FeatureCard = styled.div`
    background: white;
    padding: 2rem 1.5rem;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-4px);
    }

    .icon {
        font-size: 2.5rem;
        margin-bottom: 0.8rem;
    }

    h3 {
        font-size: 1.15rem;
        color: #7C4DFF;
        margin-bottom: 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #6a5a82;
        line-height: 1.5;
    }
`;

const InfoSection = styled.section`
    background: white;
    padding: 3rem 2rem;
    border-radius: 20px;
    margin: 3rem 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    h2 {
        color: #7C4DFF;
        font-size: 1.75rem;
        margin-bottom: 1rem;
    }

    p {
        color: #6a5a82;
        line-height: 1.7;
        margin-bottom: 0.8rem;
    }
`;

const LegalLinks = styled.section`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
    margin: 3rem 0 1rem;
    padding-top: 2rem;
    border-top: 1px solid #e0d8f0;

    a {
        color: #7C4DFF;
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const Footer = styled.footer`
    text-align: center;
    padding: 2rem 1rem 1rem;
    color: #8a83a8;
    font-size: 0.9rem;

    a {
        color: #7C4DFF;
    }
`;

const CubiePopLanding = () => {
    const features = [
        { icon: '🧩', title: 'Cube-Blast Puzzles', desc: 'Tap same-colored cube groups to pop them and clear hundreds of handcrafted levels.' },
        { icon: '💥', title: 'Boosters & Power-ups', desc: 'Unleash bombs, dynamite, and color bombs to clear the board.' },
        { icon: '❤️', title: 'Lives & Coins', desc: 'Lives regenerate over time. Earn coins and spend them on boosters.' },
        { icon: '🎁', title: 'Free to Play', desc: 'Free with optional rewarded ads and in-app purchases.' },
    ];

    return (
        <Page>
            <Container>
                <Hero>
                    <Logo>MINDWORLD GAMES</Logo>
                    <Title>CubiePop</Title>
                    <Subtitle>
                        A casual cube-blast puzzle game. Tap groups of same-colored cubes to pop them, trigger explosive combos, and reach the goal one level at a time.
                    </Subtitle>
                    <PlayBtn href="https://play.google.com/store" target="_blank" rel="noopener noreferrer">
                        ▶ Get it on Google Play (Coming Soon)
                    </PlayBtn>
                </Hero>

                <Features>
                    {features.map((f) => (
                        <FeatureCard key={f.title}>
                            <div className="icon">{f.icon}</div>
                            <h3>{f.title}</h3>
                            <p>{f.desc}</p>
                        </FeatureCard>
                    ))}
                </Features>

                <InfoSection>
                    <h2>About CubiePop</h2>
                    <p>
                        CubiePop is a free-to-play casual cube-blast puzzle game for mobile. Tap groups of two or more same-colored cubes to pop them, create powerful boosters, and reach each level's collection goal before you run out of moves. Easy to learn, satisfying to master.
                    </p>
                    <p>
                        Built by MindWorld, an indie studio focused on calming, brain-friendly mobile games.
                    </p>
                </InfoSection>

                <InfoSection>
                    <h2>Privacy & Ads</h2>
                    <p>
                        CubiePop is supported by rewarded video ads served through Unity Ads. We respect your privacy and comply with GDPR, CCPA, and Korean PIPA. You can manage advertising preferences any time in the game settings or your device settings.
                    </p>
                    <p>
                        No account registration required. Game progress is stored locally on your device, with optional cloud save and leaderboards.
                    </p>
                </InfoSection>

                <LegalLinks>
                    <Link to="/cubiepop/privacy">Privacy Policy</Link>
                    <Link to="/cubiepop/terms">Terms of Service</Link>
                    <Link to="/cubiepop/account-deletion">Data Deletion</Link>
                    <a href="mailto:mind-wellness@naver.com">Contact Support</a>
                </LegalLinks>

                <Footer>
                    © {new Date().getFullYear()} MindWorld · CubiePop · <a href="mailto:mind-wellness@naver.com">mind-wellness@naver.com</a>
                </Footer>
            </Container>
        </Page>
    );
};

export default CubiePopLanding;
